import React, { useState } from "react";
import { Send, Check } from "lucide-react";
import { submitInquiry } from "../lib/api";
import { INQUIRY } from "../constants/testIds";

const InquiryForm = ({ report = null, variant = "section" }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await submitInquiry({
        ...form,
        report_slug: report?.slug || "",
        report_title: report?.title || "",
      });
      setSubmitted(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="border border-[var(--border)] bg-[rgba(15,23,39,0.5)] p-10 text-center"
        data-testid={INQUIRY.success}
      >
        <div className="w-12 h-12 mx-auto rounded-full border border-[var(--blue)] flex items-center justify-center text-[var(--blue-soft)] mb-5">
          <Check size={20} />
        </div>
        <div className="font-mono-label text-[var(--slate)] mb-3">Inquiry received</div>
        <h3 className="font-display text-2xl text-[var(--ivory)] mb-3">
          Thank you. We&apos;ll be in touch privately.
        </h3>
        <p className="text-[var(--slate)] text-sm max-w-md mx-auto leading-relaxed font-light">
          A member of our team reviews each inquiry personally.
          Expect a response from a real person, not a sequence.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="border border-[var(--border)] bg-[rgba(15,23,39,0.45)] p-8 md:p-10"
      data-testid={INQUIRY.form}
    >
      {/* Honeypot — hidden from real users; auto-fill bots trip it and their
          submissions are silently dropped by the backend. Not display:none so
          bots still see/fill it, but off-screen + aria-hidden + tabindex -1. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={update("website")}
        />
      </div>
      {variant !== "compact" && (
        <>
          <div className="font-mono-label text-[var(--accent)] mb-3">Private Inquiry</div>
          <h3 className="font-display text-3xl md:text-4xl text-[var(--ivory)] mb-3 leading-tight">
            {report ? "Speak with the research desk." : "Begin a conversation."}
          </h3>
          <p className="text-[var(--slate)] text-sm mb-8 max-w-xl leading-relaxed">
            {report
              ? `Questions about ${report.title}, or how the analysis applies to your fleet? Send a note—replies are handled by a principal, not an inbox.`
              : "Whether you are comparing ownership versus charter, evaluating a management agreement, or rethinking your mission profile—start a private conversation with the Steck team."}
          </p>
        </>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono-label text-[var(--slate)] mb-2 block">Name</label>
          <input
            required
            className="input-field"
            value={form.name}
            onChange={update("name")}
            data-testid={INQUIRY.name}
            autoComplete="name"
          />
        </div>
        <div>
          <label className="font-mono-label text-[var(--slate)] mb-2 block">Email</label>
          <input
            required
            type="email"
            className="input-field"
            value={form.email}
            onChange={update("email")}
            data-testid={INQUIRY.email}
            autoComplete="email"
          />
        </div>
      </div>
      <div className="mt-4">
        <label className="font-mono-label text-[var(--slate)] mb-2 block">
          Organization <span className="text-[var(--slate-dim)] normal-case tracking-normal">(optional)</span>
        </label>
        <input
          className="input-field"
          value={form.company}
          onChange={update("company")}
          data-testid={INQUIRY.company}
          autoComplete="organization"
        />
      </div>
      <div className="mt-4">
        <label className="font-mono-label text-[var(--slate)] mb-2 block">Message</label>
        <textarea
          required
          rows={4}
          className="input-field"
          value={form.message}
          onChange={update("message")}
          data-testid={INQUIRY.message}
          placeholder={report ? "Reference points, fleet context, or specific questions…" : "Tell us what you are considering…"}
        />
      </div>
      {err && <div className="mt-4 text-sm text-red-300">{err}</div>}
      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs text-[var(--slate-dim)] max-w-sm leading-relaxed">
          Submissions are reviewed privately. We do not subscribe you to a list.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary"
          data-testid={INQUIRY.submit}
        >
          {submitting ? "Sending…" : "Send inquiry"} <Send size={13} />
        </button>
      </div>
    </form>
  );
};

export default InquiryForm;
